import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { userService } from "../services/user.service";
import { isAdmin } from '../utils/permissions';
import { addUserPoint, addUserPointsBulk } from "./userpoint.slice";


export const fetchUsers = createAsyncThunk("user/fetchAll", async (_, thunkAPI) => {
  try {
    return await userService.getUsers();
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch users");
  }
});

export const fetchUserById = createAsyncThunk("user/fetchById", async (id, thunkAPI) => {
  try {
    return await userService.getUserById(id);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch user");
  }
});

export const addUser = createAsyncThunk("user/add", async (user, thunkAPI) => {
  try {
    return await userService.createUser(user);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to create user");
  }
});

export const editUser = createAsyncThunk("user/edit", async ({ id, user }, thunkAPI) => {
  try {
    return await userService.updateUser(id, user);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to update user");
  }
});

export const removeUser = createAsyncThunk("user/remove", async (id, thunkAPI) => {
  try {
    await userService.deleteUser(id);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to delete user");
  }
});

export const changePassword = createAsyncThunk("user/changePassword", async ({ id, passwords }, thunkAPI) => {
  try {
    return await userService.changePassword(id, passwords);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to change password");
  }
});

export const attachUserToCustomer = createAsyncThunk(
  "user/attachToCustomer", 
  async ({ userId, customerId }, thunkAPI) => {
    try {
      return await userService.attachUserToCustomer({ userId, customerId });
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to attach user to customer");
    }
  }
);

export const importUsers = createAsyncThunk("user/import", async (file, thunkAPI) => {
  try {
    return await userService.importUsers(file);
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data || "Failed to import users");
  }
});

const userSlice = createSlice({
  name: "userCrud",
  initialState: { users: [], user: null, loading: false, error: null, showModal: false, showDeleteModal: false },
  reducers: {
     openModal: (state) => {
      state.showModal = true
    },
    closeModal: (state) => {
      state.showModal = false
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false
    },
    addUserLocally: (state, {payload}) => {
      state.users = [payload, ...state.users]
    },
    addPhotoToUser: (state, {payload}) => {
      state.users = state.users.map(user => {
        if (user.id === payload.id) {
          // If the new photo is principal, mark existing photos as non-principal
          const updatedExistingPhotos = payload.photo.isPrincipal
            ? user.photos.map(photo => ({ ...photo, isPrincipal: false }))
            : user.photos;

          return {...user, photos: [payload.photo, ...updatedExistingPhotos]};
        }
        return user;
      })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.users.push(action.payload);
      })
      .addCase(editUser.fulfilled, (state, action) => {
        const editedUser = action.meta.arg;
        state.users = state.users.map((user) => {
          if (user.id === editedUser.id) return { ...editedUser.user, id: editedUser.id };
          return user
        });
        state.showModal = false
        state.loading = false
      })
      .addCase(removeUser.pending, (state) => {
        state.loading = true
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.users = state.users.map((user) => {
          if(user.id === action.payload) return {...user, isActive: false}
          return user
        });
        state.loading = false
        state.showDeleteModal = false
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(attachUserToCustomer.pending, (state) => {
  state.loading = true;
  state.error = null;
})
.addCase(attachUserToCustomer.fulfilled, (state, action) => {
  state.loading = false;
  // Optionnel : mise à jour locale de l'utilisateur avec le customer attaché
  const userIndex = state.users.findIndex(user => user.id === action.meta.arg.userId);
  if (userIndex !== -1) {
    state.users[userIndex].attachedCustomerId = action.meta.arg.customerId;
  }
})
.addCase(attachUserToCustomer.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload;
})
      // quand un userPoint est créé pour un employé
      .addCase(addUserPoint.fulfilled, (state, action) => {
        const up = action.payload; // objet userPoint créé
        const userIndex = state.users.findIndex(u => u.id === up.employeeId);
        if (userIndex !== -1) {
          const user = state.users[userIndex];
          const prevLgts = user.lgts || [];
          state.users[userIndex] = {
            ...user,
            lgts: [...prevLgts, up],
          };
        }
      })
      .addCase(addUserPointsBulk.fulfilled, (state, action) => {
        const items = action.payload.items || action.payload.Items || [];
        if (!Array.isArray(items) || items.length === 0) return;

        items.forEach(up => {
          const userIndex = state.users.findIndex(u => u.id === up.employeeId);
          if (userIndex !== -1) {
            const user = state.users[userIndex];
            const prevLgts = user.lgts || [];
            state.users[userIndex] = {
              ...user,
              lgts: [...prevLgts, up],
            };
          }
        });
      })
      .addCase(importUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importUsers.fulfilled, (state) => {
        state.loading = false;
        // Optional: If your backend returns the newly created users, you could merge them here:
        // const newUsers = action.payload.items || action.payload.Items || [];
        // state.users = [...state.users, ...newUsers];
      })
      .addCase(importUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

;
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  addUserLocally,
  addPhotoToUser,
 // attachUserToCustomer,
} = userSlice.actions;

// Selectors
/**
 * Selector: Get users filtered by shopId (from URL)
 * Memoized to prevent unnecessary re-renders
 */
export const selectFilteredUsers = createSelector(
  [(state) => state.user.users, (state, shopId) => shopId],
  (allUsers, shopId) => {
    // If "all" or no shopId, return all users
    if (!shopId || shopId === 'all') {
      return allUsers;
    }

    // Filter users who have this shopId in their shopIds array
    return allUsers.filter(user =>
      user.shopIds && user.shopIds?.includes(shopId)
    );
  }
);

/**
 * Selector: Get users that current user has permission to see
 * Memoized to prevent unnecessary re-renders
 */
export const selectAccessibleUsers = createSelector(
  [(state) => state.user.users, (state) => state.auth.user],
  (allUsers, currentUser) => {
    if (!currentUser) return [];

    // ADMIN can see all users
    if (isAdmin(currentUser)) {
      return allUsers;
    }

    // Non-admin users only see users from their shops
    return allUsers.filter(user =>
      user.shopIds &&
      user.shopIds.some(shopId => currentUser.shopIds?.includes(shopId))
    );
  }
);

/**
 * Selector: Combined shop filtering + permission filtering
 * Memoized to prevent unnecessary re-renders
 */
export const selectFilteredAccessibleUsers = createSelector(
  [
    (state) => state.user.users,
    (state) => state.auth.user,
    (state) => state.store.stores,
    (state, shopId) => shopId
  ],
  (allUsers, currentUser, stores, shopId) => {
    if (!currentUser) return [];

    // Check if selected shop is "Administration"
    const selectedShop = stores.find(s => s.shopId === shopId);
    const isAdministrationShop = selectedShop && selectedShop.shopName === "Administration";

    // ADMIN can see all users
    if (isAdmin(currentUser)) {
      // Apply only shop filter for admins
      if (!shopId || shopId === 'all' || isAdministrationShop) {
        return allUsers;
      }
      return allUsers.filter(user =>
        user.shopIds && user.shopIds?.includes(shopId)
      );
    }

    // Non-admin: filter by both permission and shop
    let filteredUsers = allUsers.filter(user =>
      user.shopIds &&
      user.shopIds.some(id => currentUser.shopIds?.includes(id))
    );

    // Apply shop filter if not "all" and not "Administration"
    if (shopId && shopId !== 'all' && !isAdministrationShop) {
      filteredUsers = filteredUsers.filter(user =>
        user.shopIds && user.shopIds?.includes(shopId)
      );
    }

    return filteredUsers;
  }
);

export default userSlice.reducer;